/** Friendly bulk Chart-of-Accounts → Wet/Dry/Other mapping editor.
 *
 * Features:
 *   • Import CoA from Xero button (calls /api/clients/{id}/xero/import-accounts)
 *   • Search/filter by code or name
 *   • One-click classification chips (no dropdown per row)
 *   • Visible unsaved/saved state per row + "Save All" bulk action
 *   • Quick filter pills: All / Wet / Dry / Other
 */
import { useEffect, useMemo, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { MagnifyingGlass, DownloadSimple, FloppyDisk, Wine, ForkKnife, DotsThree, ArrowsClockwise } from "@phosphor-icons/react";

const CLASSIFICATIONS = [
  { v: "WET", l: "Wet", short: "WET", color: "#059669", Icon: Wine },
  { v: "DRY", l: "Dry", short: "DRY", color: "#334155", Icon: ForkKnife },
  { v: "OTHER", l: "Other (not reported)", short: "OTH", color: "#57534E", Icon: DotsThree },
];

export default function MappingTable({ clientId, siteId, canImportFromXero }) {
  const [rows, setRows] = useState([]);
  const [dirty, setDirty] = useState({}); // account_code -> true
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState("ALL");
  const [importing, setImporting] = useState(false);
  const [saving, setSaving] = useState(false);

  const scope = siteId || clientId;

  const load = useCallback(async () => {
    const { data } = await api.get(`/clients/${clientId}/mappings`, {
      params: siteId ? { site_id: siteId } : {},
    });
    setRows(data);
    setDirty({});
  }, [clientId, siteId]);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return rows.filter((m) => {
      if (filter !== "ALL" && m.classification !== filter) return false;
      if (!q) return true;
      return (
        (m.account_code || "").toLowerCase().includes(q) ||
        (m.account_name || "").toLowerCase().includes(q)
      );
    });
  }, [rows, query, filter]);

  const counts = useMemo(() => {
    const c = { ALL: rows.length, WET: 0, DRY: 0, OTHER: 0 };
    rows.forEach((m) => { if (c[m.classification] !== undefined) c[m.classification] += 1; });
    return c;
  }, [rows]);

  const setClass = (code, cls) => {
    setRows((rs) => rs.map((m) => m.account_code === code ? { ...m, classification: cls } : m));
    setDirty((d) => ({ ...d, [code]: true }));
  };

  const dirtyCount = Object.keys(dirty).length;

  const importCoA = async () => {
    setImporting(true);
    try {
      const { data } = await api.post(`/clients/${clientId}/xero/import-accounts`, null, {
        params: siteId ? { site_id: siteId } : {},
      });
      toast.success(`Imported ${data.inserted} new · refreshed ${data.refreshed} · ${data.total_xero_accounts} total in Xero`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Xero import failed");
    } finally {
      setImporting(false);
    }
  };

  const saveAll = async () => {
    if (!dirtyCount) return;
    setSaving(true);
    try {
      const dirtyRows = rows
        .filter((m) => dirty[m.account_code])
        .map((m) => ({ account_code: m.account_code, account_name: m.account_name, classification: m.classification }));
      const { data } = await api.put(`/clients/${clientId}/mappings/bulk`, {
        site_id: siteId || null,
        mappings: dirtyRows,
      });
      toast.success(`Saved ${data.updated} mapping(s)`);
      setDirty({});
    } catch {
      toast.error("Bulk save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-white border border-[#E7E5E4] rounded-md card-fade" data-testid={`mapping-table-${scope}`}>
      {/* Toolbar */}
      <div className="p-5 border-b border-[#E7E5E4] flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[220px]">
          <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#A8A29E]" />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search code or account name..."
            data-testid="mapping-search"
            className="h-9 pl-9 rounded-md text-xs"
          />
        </div>

        <div className="flex items-center bg-[#F5F5F4] rounded-md p-0.5 text-xs" data-testid="mapping-filter-pills">
          {[
            { v: "ALL", l: "All" },
            { v: "WET", l: "Wet" },
            { v: "DRY", l: "Dry" },
            { v: "OTHER", l: "Other" },
          ].map((f) => (
            <button
              key={f.v}
              onClick={() => setFilter(f.v)}
              data-testid={`mapping-filter-${f.v}`}
              className={`px-2.5 py-1.5 rounded font-medium transition-colors ${filter === f.v ? "bg-white text-[#1C1917] shadow-sm" : "text-[#57534E] hover:text-[#1C1917]"}`}
            >
              {f.l} <span className="text-[#A8A29E] ml-0.5">{counts[f.v]}</span>
            </button>
          ))}
        </div>

        {canImportFromXero && (
          <Button
            onClick={importCoA}
            disabled={importing}
            data-testid="import-coa-btn"
            variant="outline"
            className="rounded-md border-[#E7E5E4] h-9 text-xs"
          >
            <DownloadSimple size={14} className="mr-1.5" />
            {importing ? "Importing..." : "Import CoA from Xero"}
          </Button>
        )}

        <Button
          onClick={saveAll}
          disabled={!dirtyCount || saving}
          data-testid="bulk-save-mappings"
          className="bg-[#1C1917] hover:bg-[#292524] text-white rounded-md h-9 text-xs"
        >
          <FloppyDisk size={14} className="mr-1.5" />
          {saving ? "Saving..." : dirtyCount ? `Save ${dirtyCount} change${dirtyCount > 1 ? "s" : ""}` : "Save All"}
        </Button>
      </div>

      {/* Body */}
      {filtered.length === 0 ? (
        <div className="p-10 text-center text-sm text-[#A8A29E]" data-testid="mapping-empty">
          {rows.length === 0
            ? <>No accounts mapped yet. {canImportFromXero ? "Click 'Import CoA from Xero' to pull your chart of accounts." : "Connect Xero first, then import the chart of accounts."}</>
            : "No accounts match the current filter."}
        </div>
      ) : (
        <div className="divide-y divide-[#F5F5F4]">
          <div className="grid grid-cols-[100px_1fr_auto_auto] gap-4 px-5 py-3 text-[10px] uppercase tracking-[0.2em] text-[#A8A29E] font-mono">
            <span>Code</span>
            <span>Account Name</span>
            <span className="text-center">Classification</span>
            <span className="text-right pr-2">Status</span>
          </div>
          {filtered.map((m) => (
            <div
              key={m.account_code}
              className={`grid grid-cols-[100px_1fr_auto_auto] gap-4 px-5 py-2.5 items-center transition-colors ${dirty[m.account_code] ? "bg-amber-50/50" : ""}`}
              data-testid={`mapping-row-${m.account_code}`}
            >
              <span className="font-mono-num text-xs">{m.account_code}</span>
              <span className="text-sm truncate" title={m.account_name}>{m.account_name}</span>
              <div className="flex items-center gap-1" data-testid={`mapping-chips-${m.account_code}`}>
                {CLASSIFICATIONS.map((c) => {
                  const active = m.classification === c.v;
                  const Icon = c.Icon;
                  return (
                    <button
                      key={c.v}
                      onClick={() => setClass(m.account_code, c.v)}
                      data-testid={`mapping-chip-${m.account_code}-${c.v}`}
                      className={`flex items-center gap-1 px-2 py-1 rounded text-[10px] font-medium uppercase tracking-wider transition-all border ${
                        active
                          ? "text-white border-transparent shadow-sm"
                          : "text-[#57534E] bg-white border-[#E7E5E4] hover:border-[#A8A29E]"
                      }`}
                      style={active ? { backgroundColor: c.color } : undefined}
                      title={c.l}
                    >
                      <Icon size={11} weight={active ? "fill" : "duotone"} />
                      <span>{c.short}</span>
                    </button>
                  );
                })}
              </div>
              <span className="text-right pr-2 text-[10px] font-mono uppercase tracking-wider">
                {dirty[m.account_code] ? (
                  <span className="text-amber-700">unsaved</span>
                ) : (
                  <span className="text-emerald-700">saved</span>
                )}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
